// client/src/coms/components/LocalPackCard.js
// A local sticker pack (stored on this device; stickers/localStickerStore.js) in Settings → Stickers: add,
// rename, keywords, reorder, cover, delete, and "Move to Voxemera Server". Moving uploads the pack as a
// normal server-hosted pack owned by this account (keywords and order kept); it is retry-safe (the server
// recognises this pack and its stickers by their local ids) and the local copy is only removed when the
// user asks for it afterwards.
import React, { useState } from 'react';
import { comsApiPost } from '../comsApi';
import { isProbablyAnimated, uploadMultipart, uploadWithProgress } from '../imagePrep';
import { LOCAL_LIMITS, addLocalSticker, deleteLocalPack, deleteLocalSticker, reorderLocalStickers, updateLocalPack, updateLocalSticker } from '../stickers/localStickerStore';
import StickerEditorModal, { ConfirmDialog, ModalShell, StickerPreview } from './StickerEditorModal';
import StickerUploader from './StickerUploader';

const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: '8px 10px', fontSize: 13 };
const EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

export function localUploadOne(pack) {
  return async (it, prepared, onProgress) => {
    onProgress(0.4);
    await addLocalSticker(pack.id, { name: it.name.trim() || 'sticker', keywords: it.keywords, blob: prepared, mime: prepared.type, animated: isProbablyAnimated(prepared) });
    onProgress(1);
  };
}

const fileOf = (s) => new File([s.blob], `${(s.name || 'sticker').replace(/[^A-Za-z0-9_-]+/g, '_')}.${EXT[s.mime] || 'png'}`, { type: s.mime || s.blob.type });

// Upload a local pack to Voxemera Server. onProgress({ done, total, current }). Returns the server pack.
export async function moveLocalPackToServer(pack, onProgress = () => {}) {
  const stickers = pack.stickers || [];
  if (!stickers.length) throw new Error('Add at least one sticker first');
  const bad = stickers.find((s) => !s.blob || s.blob.size > LOCAL_LIMITS.maxBytes || !EXT[s.mime]);
  if (bad) throw new Error(`“${bad.name}” cannot be uploaded (PNG, JPEG, GIF or WebP up to 2 MB)`);
  const created = await comsApiPost('/stickers/packs', { name: pack.name, description: pack.description || '', visibility: 'private', local_origin_id: pack.id });
  const server = created.pack;
  let done = 0;
  for (const s of stickers) {
    const before = done;
    onProgress({ done: before, total: stickers.length, current: s.name, fraction: 0 });
    await uploadWithProgress(
      `/stickers/packs/${server.id}/stickers`,
      { name: s.name || 'sticker', keywords: (s.keywords || []).join(','), local_origin_id: s.id },
      fileOf(s),
      (fraction) => onProgress({ done: before, total: stickers.length, current: s.name, fraction })
    );
    done += 1;
  }
  const cover = stickers.find((s) => s.id === pack.cover_sticker_id);
  let final = server;
  if (cover && !server.icon_url) {
    final = ((await uploadMultipart(`/stickers/packs/${server.id}/icon`, {}, fileOf(cover)).catch(() => null)) || {}).pack || server;
  }
  onProgress({ done, total: stickers.length, current: '', fraction: 1 });
  await updateLocalPack(pack.id, { server_pack_id: final.id, moved_at: new Date().toISOString() });
  return final;
}

function MoveDialog({ pack, onClose, onMoved }) {
  const [phase, setPhase] = useState('confirm'); // confirm | moving | done | error
  const [progress, setProgress] = useState({ done: 0, total: (pack.stickers || []).length, current: '', fraction: 0 });
  const [err, setErr] = useState('');
  const [server, setServer] = useState(null);
  const [busy, setBusy] = useState(false);

  async function start() {
    setPhase('moving');
    setErr('');
    try {
      const result = await moveLocalPackToServer(pack, setProgress);
      setServer(result);
      setPhase('done');
      onMoved();
    } catch (e) {
      setErr(errText(e, 'The upload stopped. Nothing on this device was changed; you can try again.'));
      setPhase('error');
    }
  }

  const pct = progress.total ? Math.round(((progress.done + (progress.fraction || 0)) / progress.total) * 100) : 0;
  return (
    <ModalShell label={`Move ${pack.name} to Voxemera Server`} onClose={() => phase !== 'moving' && !busy && onClose()} width={500}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>Move “{pack.name}” to Voxemera Server</div>
      {phase === 'confirm' || phase === 'error' ? (
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 8, lineHeight: 1.5 }}>
          The {pack.stickers.length} sticker{pack.stickers.length === 1 ? '' : 's'} and their keywords are uploaded and stored online under your account. The pack gets a permanent ID and a share code, and you can publish it in the Marketplace. Server hosting is currently free.
          <br />Your local copy stays on this device until you choose to remove it.
        </div>
      ) : null}
      {phase === 'moving' || phase === 'error' ? (
        <div style={{ marginTop: 14 }}>
          <div className="vox-progress" role="progressbar" aria-label="Uploading the pack" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div style={{ width: `${pct}%` }} />
          </div>
          <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6 }}>
            {phase === 'moving' ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}${progress.current ? `: ${progress.current}` : ''}…` : `${progress.done} of ${progress.total} uploaded`}
          </div>
        </div>
      ) : null}
      {phase === 'done' && server ? (
        <div role="status" style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5 }}>
          <div style={{ color: '#a7f3d0', fontWeight: 800 }}>Uploaded. “{server.name}” is now on Voxemera Server.</div>
          <div style={{ opacity: 0.8, marginTop: 4 }}>Pack ID {server.id}{server.share_code ? ` · share code ${server.share_code}` : ''}. Find it under My packs, where you can share or publish it.</div>
          <div style={{ opacity: 0.8, marginTop: 8 }}>The local copy is still on this device. Remove it now, or keep it as a backup (it is hidden from the sticker picker so you do not see each sticker twice).</div>
        </div>
      ) : null}
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, flexWrap: 'wrap' }}>
        {phase === 'done' ? (
          <>
            <button type="button" className="coms-mini-btn" disabled={busy} onClick={onClose}>Keep local copy</button>
            <button type="button" className="coms-mini-btn vox-sbm-danger-outline" disabled={busy} data-testid="remove-local-copy" onClick={async () => { setBusy(true); await deleteLocalPack(pack.id); onMoved(); onClose(); }}>
              Remove local copy
            </button>
          </>
        ) : (
          <>
            <button type="button" className="coms-mini-btn" onClick={onClose} disabled={phase === 'moving'}>Cancel</button>
            <button type="button" className="coms-mini-btn primary" onClick={start} disabled={phase === 'moving'} data-testid="move-to-server-start">
              {phase === 'moving' ? 'Uploading…' : phase === 'error' ? 'Try again' : 'Move to Voxemera Server'}
            </button>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function LocalPackDetails({ pack, onClose, onSaved }) {
  const [name, setName] = useState(pack.name);
  const [description, setDescription] = useState(pack.description || '');
  const [cover, setCover] = useState(pack.cover_sticker_id || '');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    setErr('');
    try {
      await updateLocalPack(pack.id, { name, description, cover_sticker_id: cover });
      onSaved();
      onClose();
    } catch (e) {
      setErr(errText(e, 'Could not save the pack.'));
      setBusy(false);
    }
  }
  return (
    <ModalShell label={`Pack details ${pack.name}`} onClose={() => !busy && onClose()} width={500}>
      <div style={{ fontSize: 17, fontWeight: 900 }}>Pack details</div>
      <label style={{ display: 'block', marginTop: 12, fontSize: 12, fontWeight: 800 }}>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} style={{ ...field, width: '100%', boxSizing: 'border-box', marginTop: 4 }} />
      </label>
      <label style={{ display: 'block', marginTop: 10, fontSize: 12, fontWeight: 800 }}>
        Description
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} rows={3} style={{ ...field, width: '100%', boxSizing: 'border-box', marginTop: 4, resize: 'vertical', fontFamily: 'inherit' }} />
      </label>
      {pack.stickers.length ? (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 800 }}>Cover</div>
          <div role="radiogroup" aria-label="Cover sticker" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {pack.stickers.map((s) => (
              <button key={s.id} type="button" role="radio" aria-checked={cover === s.id} aria-label={`Use ${s.name} as the cover`} onClick={() => setCover(s.id)} style={{ width: 52, height: 52, padding: 3, borderRadius: 10, border: cover === s.id ? '2px solid var(--vx-link, #8db4ff)' : '2px solid transparent', background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.04 * var(--vx-ov, 1)))', cursor: 'pointer' }}>
                <img src={s.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 10 }}>{err}</div> : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="coms-mini-btn primary" onClick={save} disabled={busy || name.trim().length < 2}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </ModalShell>
  );
}

// pack: from listLocalPacks(). onChanged(): reload.
export default function LocalPackCard({ pack, onChanged, onError }) {
  const [dialog, setDialog] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState('');
  const stickers = pack.stickers || [];
  const find = (id) => stickers.find((s) => s.id === id);
  const cover = find(pack.cover_sticker_id) || stickers[0];

  async function act(fn, fallback) {
    try {
      await fn();
      await onChanged();
    } catch (e) {
      if (typeof onError === 'function') onError(errText(e, fallback));
    }
  }

  function reorder(ids) {
    act(() => reorderLocalStickers(pack.id, ids), 'Could not reorder.');
  }
  function move(id, dir) {
    const ids = stickers.map((s) => s.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder(ids);
  }
  function dropOn(targetId) {
    if (!dragId || dragId === targetId) return;
    const ids = stickers.map((s) => s.id).filter((x) => x !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    setDragId('');
    reorder(ids);
  }

  const editing = dialog && dialog.kind === 'edit' ? find(dialog.stickerId) : null;
  const previewing = dialog && dialog.kind === 'preview' ? find(dialog.stickerId) : null;
  const deleting = dialog && dialog.kind === 'delete' ? find(dialog.stickerId) : null;
  const index = editing ? stickers.indexOf(editing) : -1;
  const moved = !!pack.server_pack_id;

  return (
    <div className="vox-pack-card" data-testid={`local-pack-${pack.id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 44, height: 44, borderRadius: 11, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flex: '0 0 auto' }}>
          {cover ? <img src={cover.url} alt="" style={{ width: '90%', height: '90%', objectFit: 'contain' }} /> : <span style={{ fontSize: 18 }}>🏷️</span>}
        </div>
        <div style={{ minWidth: 0, flex: '1 1 180px' }}>
          <div style={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{pack.name}</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>by you · {stickers.length}/{LOCAL_LIMITS.maxStickersPerPack}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            <span className="vox-pack-badge local" title="Stored on this device only">💾 Local · this device</span>
            {moved ? <span className="vox-pack-badge free" title="A copy is on Voxemera Server">☁️ Also on Voxemera Server · ID {pack.server_pack_id}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="coms-mini-btn tiny primary" onClick={() => setUploading((v) => !v)} aria-expanded={uploading}>Add stickers</button>
          <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'details' })}>Details</button>
          {moved ? (
            <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'remove-local' })}>Remove local copy</button>
          ) : (
            <button type="button" className="coms-mini-btn tiny" onClick={() => setDialog({ kind: 'move' })} disabled={!stickers.length} title={stickers.length ? 'Upload this pack to Voxemera Server to share or publish it' : 'Add stickers first'} data-testid="move-to-server">
              ☁️ Move to Voxemera Server
            </button>
          )}
          <button type="button" className="coms-mini-btn tiny vox-sbm-danger-outline" onClick={() => setDialog({ kind: 'delete-pack' })}>Delete pack</button>
        </div>
      </div>
      {pack.description ? <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8, overflowWrap: 'anywhere' }}>{pack.description}</div> : null}

      {uploading ? (
        <div style={{ marginTop: 12 }}>
          <StickerUploader pack={pack} roomLeft={Math.max(0, LOCAL_LIMITS.maxStickersPerPack - stickers.length)} uploadOne={localUploadOne(pack)} destination="saved on this device" onClose={() => setUploading(false)} onUploaded={onChanged} />
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        {!stickers.length ? <div style={{ fontSize: 12, opacity: 0.6 }}>No stickers yet. Use “Add stickers”.</div> : null}
        {stickers.map((s) => {
          const kw = s.keywords || [];
          return (
            <div
              key={s.id}
              style={{ width: 92, display: 'flex', flexDirection: 'column', gap: 4, opacity: dragId === s.id ? 0.5 : 1 }}
              draggable
              onDragStart={(e) => {
                setDragId(s.id);
                try {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', s.id);
                } catch {}
              }}
              onDragEnd={() => setDragId('')}
              onDragOver={(e) => dragId && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                dropOn(s.id);
              }}
            >
              <div style={{ position: 'relative', width: 92, height: 92 }}>
                <button type="button" className="vox-sticker-tile" aria-label={`Edit ${s.name}`} title={`Edit ${s.name} (drag to reorder)`} onClick={() => setDialog({ kind: 'edit', stickerId: s.id })}>
                  <img src={s.url} alt="" loading="lazy" draggable={false} style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }} />
                </button>
                <button type="button" className="vox-sticker-tile-btn" aria-label={`View ${s.name} full size`} title="View full size" style={{ left: 3 }} onClick={() => setDialog({ kind: 'preview', stickerId: s.id })}>🔍</button>
                <button type="button" className="vox-sticker-tile-btn" aria-label={`Delete ${s.name}`} title="Delete sticker" style={{ right: 3 }} onClick={() => setDialog({ kind: 'delete', stickerId: s.id })}>✕</button>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.name}>{s.name}</div>
              {kw.length ? (
                <div className="vox-sticker-keywords" title={kw.join(', ')}>{kw.join(' · ')}</div>
              ) : (
                <button type="button" className="vox-sticker-add-kw" onClick={() => setDialog({ kind: 'edit', stickerId: s.id })}>+ Add keywords</button>
              )}
            </div>
          );
        })}
      </div>

      {editing ? (
        <StickerEditorModal
          key={editing.id}
          sticker={{ ...editing, local: true }}
          pack={{ name: pack.name, can_edit: true }}
          persist={({ name, keywords }) => updateLocalSticker(editing.id, { name, keywords })}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await onChanged();
          }}
          onPreview={() => setDialog({ kind: 'preview', stickerId: editing.id, back: editing.id })}
          onMove={(dir) => move(editing.id, dir)}
          canMoveEarlier={index > 0}
          canMoveLater={index >= 0 && index < stickers.length - 1}
        />
      ) : null}
      {previewing ? <StickerPreview sticker={previewing} onClose={() => setDialog(dialog.back ? { kind: 'edit', stickerId: dialog.back } : null)} /> : null}
      {deleting ? (
        <ConfirmDialog
          title="Delete sticker?"
          message={`“${deleting.name}” will be removed from this device. Messages where you already sent it keep showing it.`}
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await act(() => deleteLocalSticker(deleting.id), 'Delete failed.');
            setDialog(null);
          }}
        />
      ) : null}
      {dialog && dialog.kind === 'delete-pack' ? (
        <ConfirmDialog
          title="Delete local pack?"
          message={`“${pack.name}” and its ${stickers.length} sticker${stickers.length === 1 ? '' : 's'} will be removed from this device.${moved ? ' The copy on Voxemera Server is not affected.' : ' There is no other copy.'} Messages where you already sent them keep showing them.`}
          confirmLabel="Delete pack"
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await act(() => deleteLocalPack(pack.id), 'Delete failed.');
            setDialog(null);
          }}
        />
      ) : null}
      {dialog && dialog.kind === 'remove-local' ? (
        <ConfirmDialog
          title="Remove the local copy?"
          message={`“${pack.name}” stays on Voxemera Server (pack ID ${pack.server_pack_id}) and keeps working everywhere. Only the copy on this device is removed.`}
          confirmLabel="Remove local copy"
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await act(() => deleteLocalPack(pack.id), 'Could not remove the local copy.');
            setDialog(null);
          }}
        />
      ) : null}
      {dialog && dialog.kind === 'details' ? <LocalPackDetails pack={pack} onClose={() => setDialog(null)} onSaved={onChanged} /> : null}
      {dialog && dialog.kind === 'move' ? <MoveDialog pack={pack} onClose={() => setDialog(null)} onMoved={onChanged} /> : null}
    </div>
  );
}
